import React, { Fragment, useEffect, useState } from "react";
import { Card, Col, Modal, Row } from "react-bootstrap";
import { AgGridReact } from "ag-grid-react";
import Select from "react-select";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import { salaryComponentList, salaryStructureCreate, salaryStructureDelete, salaryStructureList, salaryStructureView } from "../../../services/payroll";
import { studesilist } from "../../../services/master";
import { teacherlist } from "../../../services/teacher";
import { workerlist } from "../../../services/admin";

const EMP_TYPES  = [{ value: "teacher", label: "Teacher" }, { value: "worker", label: "Worker" }];
const CALC_TYPES = [{ value: "fixed", label: "Fixed" }, { value: "percentage", label: "Percentage" }];
const emptyRow   = { component_id: "", fixed_amount: "", calc_type: CALC_TYPES[0] };

export default function SalaryStructure() {
  const [list, setList]               = useState([]);
  const [compOptions, setCompOptions] = useState([]);
  const [desigOptions, setDesigOptions] = useState([]);
  const [teacherOptions, setTeacherOptions] = useState([]);
  const [workerOptions, setWorkerOptions]   = useState([]);
  const [show, setShow]               = useState(false);
  const [sid, setSid]                 = useState(0);
  const [structureName, setStructureName] = useState("");
  const [empType, setEmpType]         = useState("");
  const [designation, setDesignation] = useState("");
  const [employee, setEmployee]       = useState("");
  const [rows, setRows]               = useState([{ ...emptyRow }]);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    loadList();
    const temp = new FormData();
    if (localStorage.getItem("Role") === "1") temp.append("academy_id", localStorage.getItem("AAcademy") || "");
    salaryComponentList(temp).then((res) => {
      if (res?.status) setCompOptions(res.data.map((c) => ({ value: c.id, label: `${c.component_name} (${c.component_type})`, type: c.component_type })));
    });
    studesilist().then((res) => {
      if (res?.status) setDesigOptions((res.data || []).map((d) => ({ value: d.id, label: d.desi_name })));
    });
    // Active teachers (employee_id = teacher_id; carry their designation)
    teacherlist(1).then((res) => {
      if (res?.status) setTeacherOptions((res.data?.details || []).map((t) => ({
        value: t.teacher_id, label: t.teacher_name,
        desig_id: t.desig_id || null, desig_name: t.designation || "",
      })));
    });
    // Active workers (employee_id = user_id)
    const wtemp = new FormData();
    wtemp.append("status", 1);
    if (localStorage.getItem("Role") === "1") wtemp.append("academy_id", localStorage.getItem("AAcademy") || "");
    workerlist(wtemp).then((res) => {
      if (res?.status) setWorkerOptions((res.data?.details || []).map((w) => ({ value: w.user_id, label: w.worker_name })));
    });
  }, []);

  // Employee options depend on the selected type
  const employeeOptions = empType?.value === "teacher" ? teacherOptions
                        : empType?.value === "worker"  ? workerOptions
                        : [];

  function loadList() {
    const temp = new FormData();
    if (localStorage.getItem("Role") === "1") temp.append("academy_id", localStorage.getItem("AAcademy") || "");
    salaryStructureList(temp).then((res) => { if (res?.status) setList(res.data); });
  }

  function handleAdd() {
    setSid(0); setStructureName(""); setEmpType(""); setDesignation(""); setEmployee(""); setRows([{ ...emptyRow }]); setErrorMessage(""); setShow(true);
  }

  function handleEdit(id) {
    setSid(id); setErrorMessage(""); setShow(true);
    salaryStructureView(id).then((res) => {
      if (res?.status) {
        const d = res.data;
        setStructureName(d.structure_name);
        setEmpType(EMP_TYPES.find((t) => t.value === d.employee_type) || "");
        setDesignation(d.designation_id
          ? (desigOptions.find((o) => o.value === d.designation_id) || { value: d.designation_id, label: d.designation?.desi_name || "" })
          : "");
        setEmployee(d.employee_id
          ? { value: d.employee_id, label: d.employee_name || `Employee #${d.employee_id}` }
          : "");
        setRows(d.items?.map((item) => ({
          component_id: compOptions.find((c) => c.value === item.component_id) || { value: item.component_id, label: item.component?.component_name || "" },
          fixed_amount: item.fixed_amount,
          calc_type:    CALC_TYPES.find((t) => t.value === item.calc_type) || CALC_TYPES[0],
        })) || [{ ...emptyRow }]);
      }
    });
  }

  function handleClose() { setShow(false); }

  function handleDelete(id) {
    Swal.fire({ text: "Delete this structure?", icon: "warning", showCancelButton: true, confirmButtonColor: "#ac0404", cancelButtonColor: "#6259ca", confirmButtonText: "Delete", allowOutsideClick: false })
      .then((result) => {
        if (result.isConfirmed) {
          salaryStructureDelete(id).then((res) => {
            if (res?.status) { toast.success(res.message); loadList(); }
          });
        }
      });
  }

  function handleEmployee(e) {
    setEmployee(e);
    // Single-employee structure derives the designation from the chosen teacher
    if (e?.desig_id) setDesignation({ value: e.desig_id, label: e.desig_name || "" });
    else if (e) setDesignation(""); // worker / teacher without designation
  }

  function updateRow(i, field, value) {
    setRows((prev) => prev.map((r, idx) => idx === i ? { ...r, [field]: value } : r));
  }

  function handleSubmit() {
    setErrorMessage("");
    if (!structureName.trim()) { setErrorMessage("Please enter structure name"); return; }
    if (!empType)              { setErrorMessage("Please select employee type"); return; }
    if (rows.some((r) => !r.component_id)) { setErrorMessage("Please select component for all rows"); return; }

    const payload = {
      id: sid, structure_name: structureName, employee_type: empType.value,
      employee_id: employee?.value || null,
      designation_id: designation?.value || null,
      items: rows.map((r) => ({
        component_id: r.component_id?.value || r.component_id,
        fixed_amount: r.fixed_amount || 0,
        calc_type:    r.calc_type?.value || "fixed",
      })),
    };
    if (localStorage.getItem("Role") === "1") payload.academy_id = localStorage.getItem("AAcademy") || "";

    salaryStructureCreate(payload).then((res) => {
      if (res?.status) { toast.success(res.message); handleClose(); loadList(); }
      else setErrorMessage(res?.message || "Something went wrong");
    });
  }

  const columnDefs = [
    { headerName: "Structure Name", field: "structure_name", sortable: true, filter: true, floatingFilter: true, resizable: true, cellStyle: { textAlign: "left" } },
    { headerName: "Employee Type",  field: "employee_type",  width: 140, resizable: true,
      cellRenderer: (p) => <span className={`badge ${p.value === "teacher" ? "bg-primary" : "bg-info"}`}>{p.value}</span> },
    { headerName: "Designation",    field: "designation.desi_name", width: 150, resizable: true, cellStyle: { textAlign: "left" } },
    { headerName: "Employee",       field: "employee_name", width: 160, resizable: true, cellStyle: { textAlign: "left" },
      cellRenderer: (p) => p.value || "—" },
    { headerName: "Components",     field: "items", width: 100, resizable: true,
      cellRenderer: (p) => <span>{p.value?.length || 0} items</span> },
    {
      headerName: "Actions", field: "id", width: 120, filter: false, resizable: true,
      cellRenderer: (p) => (
        <div className="text-center">
          <a onClick={() => handleEdit(p.value)} className="btn-sm btn-info me-2"><i className="fa fa-edit" /></a>
          <a onClick={() => handleDelete(p.value)} className="btn-sm btn-danger"><i className="fa fa-trash" /></a>
        </div>
      ),
    },
  ];

  return (
    <Fragment>
      <div className="page-header reported-header">
        <div>
          <h2 className="main-content-title tx-24 mg-b-5">Salary Structure</h2>
          <ol className="breadcrumb">
            <li className="breadcrumb-item"><a>Payroll</a></li>
            <li className="breadcrumb-item active" aria-current="page">Salary Structure</li>
          </ol>
        </div>
      </div>

      <Row className="row-sm mx-0 mt-3">
        <Col xxl={12} lg={12} md={12}>
          <Card className="custom-card overflow-hidden">
            <Card.Body>
              <div className="responsive">
                <div className="res-right">
                  <a onClick={handleAdd} className="btn my-1 me-2 btn-success tx-teko">Add</a>
                </div>
                <div className="ag-theme-alpine ag-style">
                  <AgGridReact columnDefs={columnDefs} rowData={list} pagination paginationPageSize={10} suppressDragLeaveHidesColumns />
                </div>
              </div>

              <Modal show={show} onHide={handleClose} size="lg">
                <Modal.Header><h4>{sid === 0 ? "Add" : "Edit"} Salary Structure</h4></Modal.Header>
                <Modal.Body>
                  <Row className="row-sm mx-0">
                    <Col lg={4} md={6} className="mb-3">
                      <label className="mb-2">Structure Name <span className="text-danger">*</span></label>
                      <input type="text" className="form-control" placeholder="e.g. Teacher Basic" value={structureName} onChange={(e) => setStructureName(e.target.value)} />
                    </Col>
                    <Col lg={4} md={6} className="mb-3">
                      <label className="mb-2">Employee Type <span className="text-danger">*</span></label>
                      <Select className="default-select" placeholder="Select Type" options={EMP_TYPES} value={empType}
                        onChange={(e) => { setEmpType(e); setEmployee(""); }} isClearable />
                    </Col>
                    <Col lg={4} md={6} className="mb-3">
                      <label className="mb-2">Designation <span className="text-muted" style={{ fontSize: "11px" }}>{employee ? "(from employee)" : "(optional)"}</span></label>
                      <Select className="default-select" placeholder="Select Designation" options={desigOptions} value={designation} onChange={(e) => setDesignation(e)} isSearchable isClearable isDisabled={!!employee} />
                    </Col>
                    <Col lg={4} md={6} className="mb-3">
                      <label className="mb-2">Specific Employee <span className="text-muted" style={{ fontSize: "11px" }}>(optional — overrides designation)</span></label>
                      <Select className="default-select"
                        placeholder={empType ? "Select Employee" : "Select type first"}
                        options={employeeOptions} value={employee}
                        onChange={handleEmployee} isSearchable isClearable isDisabled={!empType} />
                    </Col>
                  </Row>
                  <p className="text-muted mb-2" style={{ fontSize: "12px" }}>
                    Priority at payroll generation: <b>Specific Employee</b> → <b>Designation</b> → <b>All (type)</b>.
                    For workers (no designation), pick the specific employee.
                  </p>

                  <div className="table-responsive mt-2">
                    <table className="table table-bordered">
                      <thead style={{ backgroundColor: "#2d4ac6" }}>
                        <tr>
                          <th style={{ color: "#fff", width: "45%" }}>Component</th>
                          <th style={{ color: "#fff", width: "25%" }}>Fixed Amount</th>
                          <th style={{ color: "#fff", width: "20%" }}>Calc Type</th>
                          <th style={{ color: "#fff", width: "10%" }} className="text-center">Del</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map((row, i) => (
                          <tr key={i}>
                            <td>
                              <Select className="default-select" placeholder="Select Component" options={compOptions}
                                value={row.component_id || null} onChange={(e) => updateRow(i, "component_id", e)} isClearable />
                            </td>
                            <td>
                              <input type="number" className="form-control" placeholder="0.00"
                                value={row.fixed_amount} onChange={(e) => updateRow(i, "fixed_amount", e.target.value)} />
                            </td>
                            <td>
                              <Select className="default-select" options={CALC_TYPES}
                                value={row.calc_type} onChange={(e) => updateRow(i, "calc_type", e)} />
                            </td>
                            <td className="text-center">
                              {rows.length > 1 && (
                                <a onClick={() => setRows((prev) => prev.filter((_, idx) => idx !== i))} className="btn btn-sm btn-danger">
                                  <i className="fa fa-times" />
                                </a>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr>
                          <td colSpan={4}>
                            <a onClick={() => setRows((prev) => [...prev, { ...emptyRow }])} className="btn btn-sm btn-success">
                              <i className="fa fa-plus me-1" /> Add Row
                            </a>
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>

                  {errorMessage && <span className="text-danger">{errorMessage}</span>}
                </Modal.Body>
                <Modal.Footer>
                  <a onClick={handleSubmit} className="btn btn-success">{sid === 0 ? "Submit" : "Update"}</a>
                  <a onClick={handleClose} className="btn btn-secondary">Close</a>
                </Modal.Footer>
              </Modal>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Fragment>
  );
}
